import React, {useContext, useEffect, useState} from 'react';
import {Box, CircularProgress, Collapse, List, ListItemButton, ListItemIcon, ListItemText,} from '@mui/material';
import {ChevronRight, ExpandMore, Folder} from '@mui/icons-material';
import APIWrapperContext from '../../../services/APIWrapperContext';
import {DocumentsService} from '../../../services/openapi/services/DocumentsService';
import {DirView} from '../../../services/openapi';

interface FolderNode {
    hidrive_id: string;
    name: string;
    parent: string;
    fullPath: string;
    children?: FolderNode[];
    loaded?: boolean;
}

interface FolderTreePickerProps {
    selectedFolderId: string | null;
    onSelectFolder: (folderId: string, folderName: string, folderPath: string, currentFullPath: string) => void;
    currentFileId: string; // To prevent selecting the same file/folder
}

export const FolderTreePicker: React.FC<FolderTreePickerProps> = ({
                                                                      selectedFolderId,
                                                                      onSelectFolder,
                                                                      currentFileId,
                                                                  }) => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [rootFolders, setRootFolders] = useState<FolderNode[]>([]);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [currentFullPath, setCurrentFullPath] = useState<string>('');

    // Load root folders
    useEffect(() => {
        setLoading(true);
        call_backend_api(
            DocumentsService.hidriveGetFilesDocGetDirContentGet(undefined, false),
            (res: DirView) => {
                const folders = (res.children || [])
                    .filter((item) => item.type === 'dir')
                    .map((item) => ({
                        hidrive_id: item.hidrive_id,
                        name: item.parent.split('/').pop() || item.parent,
                        parent: item.parent,
                        fullPath: item.parent,
                        children: [],
                        loaded: false,
                    }));
                setRootFolders(folders);
                setLoading(false);
            },
            () => {
                setLoading(false);
            },
            undefined,
            false
        );
    }, [call_backend_api]);

    const loadSubfolders = (
        folder: FolderNode,
        updateTree: (updateFn: (folders: FolderNode[]) => FolderNode[]) => void
    ) => {
        call_backend_api(
            DocumentsService.hidriveGetFilesDocGetDirContentGet(folder.hidrive_id, false),
            (res: DirView) => {
                const subfolders = (res.children || [])
                    .filter((item) => item.type === 'dir')
                    .map((item) => {
                        // Build full path from parent folder's path + current folder name
                        const folderName = item.parent.split('/').pop() || item.parent;
                        const fullPath = folder.fullPath + '/' + item.parent;

                        return {
                            hidrive_id: item.hidrive_id,
                            name: folderName,
                            parent: res.parent,
                            fullPath: fullPath,
                            children: [],
                            loaded: false,
                        };
                    });

                const updateFolderChildren = (folders: FolderNode[]): FolderNode[] => {
                    return folders.map((f) => {
                        if (f.hidrive_id === folder.hidrive_id) {
                            return {...f, children: subfolders, loaded: true};
                        } else if (f.children && f.children.length > 0) {
                            return {...f, children: updateFolderChildren(f.children)};
                        }
                        return f;
                    });
                };

                updateTree(updateFolderChildren);
            },
            undefined,
            undefined,
            false
        );
    };

    const handleToggleExpand = (folderId: string, folder: FolderNode) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(folderId)) {
            newExpanded.delete(folderId);
        } else {
            newExpanded.add(folderId);
            // Load subfolders if not already loaded
            if (!folder.loaded) {
                loadSubfolders(folder, (updateFn) => {
                    setRootFolders((prev) => updateFn(prev));
                });
            }
        }
        setExpandedFolders(newExpanded);
    };

    const renderFolderTree = (folders: FolderNode[], level: number = 0): React.ReactNode[] => {
        return folders.map((folder) => {
            const isExpanded = expandedFolders.has(folder.hidrive_id);
            const isSelected = selectedFolderId === folder.hidrive_id;
            const isDisabled = folder.hidrive_id === currentFileId;

            return (
                <React.Fragment key={folder.hidrive_id}>
                    <ListItemButton
                        selected={isSelected}
                        disabled={isDisabled}
                        sx={{pl: 2 + level * 2}}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) {
                                setCurrentFullPath(folder.fullPath);
                                onSelectFolder(folder.hidrive_id, folder.name, folder.fullPath, folder.fullPath);
                            }
                        }}>
                        <ListItemIcon
                            sx={{minWidth: 32}}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(folder.hidrive_id, folder);
                            }}>
                            {isExpanded ? <ExpandMore/> : <ChevronRight/>}
                        </ListItemIcon>
                        <ListItemIcon sx={{minWidth: 32}}>
                            <Folder color={isSelected ? 'primary' : 'inherit'}/>
                        </ListItemIcon>
                        <ListItemText
                            primary={folder.name}
                            primaryTypographyProps={{
                                style: {
                                    wordBreak: 'break-word',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                            }}
                        />
                    </ListItemButton>
                    {folder.children && folder.children.length > 0 && (
                        <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                            <List component='div' disablePadding>
                                {renderFolderTree(folder.children, level + 1)}
                            </List>
                        </Collapse>
                    )}
                </React.Fragment>
            );
        });
    };

    if (loading) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <Box sx={{maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1}}>
            <List component='nav' disablePadding>
                {renderFolderTree(rootFolders)}
            </List>
        </Box>
    );
};
